import api from "./api";
import ContractMoveOut, { ContractMoveOutDTO } from "@/model/contractMoveOut";
import PaginateModel, { PaginateDTO } from "@/model/paginate";
import { ContractMovingOutHistoryItem } from "@/model/contract";
import _ from "lodash";

interface MoveOutList {
  paginate: PaginateDTO;
  list: ContractMoveOut[];
}

interface MoveOutListResponseInterface {
  meta: string;
  data: {
    data: ContractMoveOutDTO[];
    pagination: any;
  };
}

interface ContractMovingOutHistoryPointList {
  paginate: PaginateDTO;
  list: ContractMovingOutHistoryItem[];
}

class ContractMoveOutApi extends api {
  constructor() {
    super();
    this.setup();
  }

  async store(contract: ContractMoveOut) {
    const response = await this.getHttp().post<any>("/contract-moving-outs", contract);
    return response.data;
  }

  async accept(contract: ContractMoveOut) {
    const response = await this.getHttp().put<any>("/contract-moving-outs/" + contract.id + "/accept", {
      accepted_by: contract.accepted_by
    });
    return response.data;
  }

  async updateMoveOutDate(contract: ContractMoveOut) {
    const response = await this.getHttp().put<any>("/contract-moving-outs/" + contract.id + "/move-out-date", {
      moving_out_date: contract.moving_out_date
    });
    return response.data;
  }

  async reject(contract: ContractMoveOut) {
    const response = await this.getHttp().put<any>("/contract-moving-outs/" + contract.id + "/reject");
    return response.data;
  }

  async goBack(contract: ContractMoveOut, data) {
    const response = await this.getHttp().put<any>("/contract-moving-outs/" + contract.id + "/back", data);
    return response.data;
  }

  async checkList(contract: ContractMoveOut) {
    const contractData = Object.assign({}, contract);
    const response = await this.getHttp().put<any>("/contract-moving-outs/" + contractData.id + "/check-list", {
      return_item_ids: contractData.return_item_ids,
      contract_moving_out_check_list: contractData.contract_moving_out_check_list.map((item: any) => {
        item.repair_image_id = item.repair_image_id.map(image => image.id);
        return item;
      })
    });
    return response.data;
  }

  async signature(contract: ContractMoveOut) {
    const response = await this.getHttp().put<any>("/contract-moving-outs/" + contract.id + "/signature", contract.contract_signature);
    return response.data;
  }

  async reSignature(contract: ContractMoveOut) {
    const response = await this.getHttp().put<any>("/contract-moving-outs/" + contract.id + "/re-signature");
    return response.data;
  }

  async correctCheckList(contract: ContractMoveOut) {
    const contractData = _.cloneDeep(contract);
    contractData.contract_moving_out_check_list = contractData.contract_moving_out_check_list.map((item: any) => {
      item.repair_image_id = item.repair_image_id.map(image => image.id);
      return item;
    });
    const response = await this.getHttp().put<any>("/contract-moving-outs/" + contractData.id + "/correct-check-list", {
      message: contractData.message,
      is_resolved: contractData.is_resolved,
      contract_moving_out_check_list: contractData.contract_moving_out_check_list,
      is_changed: contractData.is_changed,
    });
    return response.data;
  }

  async repairRequest(contract: ContractMoveOut) {
    const response = await this.getHttp().put<any>("/contract-moving-outs/" + contract.id + "/repair-request");
    return response.data;
  }

  async repairRequestProcess(contract: ContractMoveOut) {
    const response = await this.getHttp().put<any>("/contract-moving-outs/" + contract.id + "/repair-request-process", {
      request_repair_witness_in_charge_id: contract.request_repair_witness_in_charge_id,
      request_repair_manage_in_charge_id: contract.request_repair_manage_in_charge_id,
      request_repair_date: contract.request_repair_date,
      request_repair_witness_id: contract.request_repair_witness_id,
    });
    return response.data;
  }

  async approvalRepairRequest(contract: ContractMoveOut) {
    const response = await this.getHttp().put<any>("/contract-moving-outs/" + contract.id + "/approval-repair-request");
    return response.data;
  }


  async schedule(contract: ContractMoveOut) {
    const data = {
      in_charge_id: contract.in_charge_id,
      witness_id: contract.witness_id,
      witness_owner_id: contract.witness_owner_id,
      desired_date: contract.desired_date,
    };
    const response = await this.getHttp().put<any>("/contract-moving-outs/" + contract.id + "/schedule", data);
    return response.data;
  }

  async updateReport(contract: ContractMoveOut) {
    const response = await this.getHttp().put<any>("/contract-moving-outs/" + contract.id + "/repair-report", {
      repair_content: contract.repair_content.map(item => item.tranformRequest()),
      report_in_charge_id: contract.report_in_charge_id,
      report_repair_date: contract.report_repair_date,
      billing_method: contract.billing_method,
      breakdown_offset: contract.breakdown_offset,
      report_remarks: contract.report_remarks,
      is_resolved: contract.is_resolved,
      message: contract.message,
      is_send_mail: contract.is_send_mail,
    });
    return response.data;
  }


  async index(query): Promise<MoveOutList> {
    const data: any = {};
    if (query.notLoading) {
      data.notLoading = true;
    }
    const response = await this.getHttp().get<MoveOutListResponseInterface>(
      "/contract-moving-outs",
      {
        params: query,
        data: data
      }
    );

    const list = response.data.data.data.map((item) => {
      return new ContractMoveOut(item);
    });
    const paginate = new PaginateModel({
      page: response.data.data.pagination.current_page,
      pageCount: response.data.data.pagination.last_pages,
      start: response.data.data.pagination.current_page,
      end: response.data.data.pagination.last_pages,
      count: response.data.data.pagination.total,
    });
    return {
      list,
      paginate,
    };
  }

  async show(id) {
    const response = await this.getHttp().get<any>("/contract-moving-outs/" + id);
    return response.data;
  }

  async getMoveOutDateByProperty(id) {
    const response = await this.getHttp().get<any>("/contract-moving-outs/" + id +  "/get-move-out");

    return response.data.data.move_out_date;
  }

  async getHistory(id, query): Promise<ContractMovingOutHistoryPointList> {
    const response = await this.getHttp().get<any>(
      "/contract-moving-outs/" + id + "/history",
      {
        params: query,
      }
    );

    if (response.status === 200) {
      const list = response.data.data.data.map((item) => {
        return new ContractMovingOutHistoryItem(item);
      });

      const paginate = new PaginateModel({
        page: response.data.data.pagination.current_page,
        pageCount: response.data.data.pagination.last_pages,
        start: response.data.data.pagination.current_page,
        end: response.data.data.pagination.last_pages,
        count: response.data.data.pagination.total,
      });

      return {
        list,
        paginate,
      };
    }
  }
}
export default new ContractMoveOutApi();
